import { createBrowserRouter, RouterProvider, Navigate, Outlet, UIMatch } from "react-router";
import { observer } from "mobx-react-lite";
import { useStore } from "@/hooks/useStore";
import { useSessionInitialization } from "./useSessionInitialization";
import { Modal } from "./Modal";
import { Initialization } from "@/pages/Initialization";
import { CustomRouteHandle } from "@/types/router";
import { Routes } from "@/config/routes";

import { Authentication } from "@/layouts/Authentication";
import { Dashboard } from "@/layouts/Dashboard";
import { Empty } from "@/layouts/Empty";

import { Login } from "@/pages/Authentication/Login";
import { ForgotPassword } from "@/pages/Authentication/ForgotPassword";
import { ResetPassword } from "@/pages/Authentication/ResetPassword";
import { Onboard } from "@/pages/Authentication/Onboard";
import { SSO } from "@/pages/Authentication/SSO";

import { Home } from "@/pages/Dashboard/Home";
import { InfiltrationInflow } from "@/pages/Dashboard/InfiltrationInflow";
import { Events } from "@/pages/Dashboard/Events";
import { Overflow } from "@/pages/Dashboard/Overflow";
import { Precipitation } from "@/pages/Dashboard/Precipitation";
import { PumpingStations } from "@/pages/Dashboard/PumpingStations";
import { UserGroups } from "@/pages/Dashboard/Adminstration/UserGroups";
import { AddUserGroup } from "@/pages/Dashboard/Adminstration/UserGroups/AddUserGroup";
import { EditUserGroup } from "@/pages/Dashboard/Adminstration/UserGroups/EditUserGroup";
import { Users } from "@/pages/Dashboard/Adminstration/Users";
import { Map } from "@/pages/Dashboard/Map";
import { Structures } from "@/pages/Dashboard/Structures";
import { Groups } from "@/pages/Dashboard/Adminstration/Groups";
import { Organizations } from "@/pages/Dashboard/Backoffice/Organizations";
import { BackofficeUsers } from "@/pages/Dashboard/Backoffice/Users";
import { Devices } from "@/pages/Dashboard/Adminstration/Devices";
import { Billing } from "@/pages/Dashboard/Adminstration/Billing";
import { Settings } from "@/pages/Dashboard/Adminstration/Settings";
import { NotFound } from "@/pages/NotFound";
import { Structure } from "@/pages/Dashboard/Structures/Structure";
import { Properties } from "@/pages/Dashboard/Structures/Properties";
import { useEffect } from "react";
import { SideSheet } from "./SideSheet";
import { Blockage } from "@/pages/Dashboard/Structures/Blockages/Blockage";
import { Blockages } from "@/pages/Dashboard/Blockages";
import { Alarms } from "@/pages/Dashboard/Alarms";

const ProtectedRoute = observer(({ children }: { children: React.ReactNode }) => {
	const { userStore } = useStore();
	return userStore.isLoggedIn ? children : <Navigate to={Routes.Auth.Login.path} replace />;
});

// Top-level layout that hosts the global Modal and SideSheet alongside the routed page.
// Mounting them here (instead of as siblings of <RouterProvider />) ensures their content
// renders inside the Router context, so registry-rendered modals can use react-router hooks
// like useSearchParams / useLocation.
const RootLayout = () => (
	<>
		<Outlet />
		<Modal />
		<SideSheet />
	</>
);

const AuthenticationRoute = observer(({ children }: { children: React.ReactNode }) => {
	const { userStore } = useStore();
	return !userStore.isLoggedIn ? children : <Navigate to={Routes.Dashboard.Home.path} replace />;
});

const router = createBrowserRouter([
	{
		element: <RootLayout />,
		children: [
			{
				element: (
					<AuthenticationRoute>
						<Authentication />
					</AuthenticationRoute>
				),
				children: [
					{ path: Routes.Auth.Login.path, element: <Login /> },
					{ path: Routes.Auth.ForgotPassword.path, element: <ForgotPassword /> },
					{ path: Routes.Auth.ResetPassword.path, element: <ResetPassword /> },
					{ path: Routes.Auth.Onboard.path, element: <Onboard /> },
					{ path: Routes.Auth.SSO.path, element: <SSO /> },
				],
			},
			{
				element: (
					<ProtectedRoute>
						<Dashboard />
					</ProtectedRoute>
				),
				handle: { layout: "default" } satisfies CustomRouteHandle,
				children: [
					{
						index: true,
						path: Routes.Dashboard.Home.path,
						element: <Home />,
						handle: {
							breadcrumbs: [
								{ label: "Overview", path: Routes.Dashboard.Home.path },
								{ label: "Home", path: Routes.Dashboard.Home.path },
							],
						} satisfies CustomRouteHandle,
					},
					{
						path: Routes.Dashboard.InfiltrationInflow.path,
						element: <InfiltrationInflow />,
						handle: {
							breadcrumbs: [
								{ label: "Home", path: Routes.Dashboard.Home.path },
								{ label: "Infiltration & Inflow", path: Routes.Dashboard.InfiltrationInflow.path },
							],
						},
					},
					{
						path: Routes.Dashboard.Blockages.path,
						element: <Blockages />,
						handle: {
							breadcrumbs: [
								{ label: "Home", path: Routes.Dashboard.InfiltrationInflow.path },
								{ label: "Blockages", path: Routes.Dashboard.InfiltrationInflow.path },
							],
						},
					},
					{
						path: Routes.Dashboard.Alarms.path,
						element: <Alarms />,
						handle: {
							breadcrumbs: [
								{ label: "Overview", path: Routes.Dashboard.Home.path },
								{ label: "Alarms", path: Routes.Dashboard.Alarms.path },
							],
						},
					},
					{
						path: Routes.Dashboard.Events.path,
						element: <Events />,
						handle: { breadcrumb: "Events" },
					},
					{
						path: Routes.Dashboard.Overflow.path,
						element: <Overflow />,
						handle: { breadcrumb: "Overflow" },
					},
					{
						path: Routes.Dashboard.Precipitation.path,
						element: <Precipitation />,
						handle: { breadcrumb: "Precipitation" },
					},
					{
						path: Routes.Dashboard.PumpingStations.path,
						element: <PumpingStations />,
						handle: { breadcrumb: "Pumping Stations" },
					},
					{
						path: Routes.Dashboard.Admin.UserGroups.path,
						element: <UserGroups />,
						handle: { breadcrumb: "User Groups" },
					},
					{
						path: Routes.Dashboard.Admin.AddUserGroup.path,
						element: <AddUserGroup />,
						handle: { breadcrumb: "Add User Group" },
					},
					{
						path: Routes.Dashboard.Admin.EditUserGroup.path,
						element: <EditUserGroup />,
						handle: {
							breadcrumb: (match: UIMatch) => `Edit Group (${match.params.userGroupId})`,
						},
					},
					{
						path: Routes.Dashboard.Admin.Users.path,
						element: <Users />,
						handle: {
							breadcrumbs: [
								{ label: "Organization", path: Routes.Dashboard.Admin.Users.path },
								{ label: "Users", path: Routes.Dashboard.Admin.Users.path },
							],
						},
					},
					{
						path: Routes.Dashboard.Map.path,
						element: <Map />,
						handle: { layout: "full", breadcrumb: "Map" },
					},
					{
						path: Routes.Dashboard.Structures.Structures.path,
						element: <Structures />,
						handle: {
							breadcrumbs: [
								{ label: "Home", path: Routes.Dashboard.Home.path },
								{ label: "Structures", path: Routes.Dashboard.Structures.Structures.path },
							],
						},
					},
					{
						path: Routes.Dashboard.Structures.Structure.path,
						element: <Structure />,
						handle: {
							breadcrumbs: [
								{ label: "Home", path: Routes.Dashboard.Home.path },
								{ label: "Structures", path: Routes.Dashboard.Structures.Structures.path },
								{
									label: (match: UIMatch) => `Structure ${match.params.id}`,
									path: (match: UIMatch) =>
										Routes.Dashboard.Structures.Structure.build({
											id: match.params.id as Dryp.StructureId,
										}),
								},
							],
						},
					},
					{
						path: Routes.Dashboard.Structures.Blockage.path,
						element: <Blockage />,
					},
					{
						path: Routes.Dashboard.Structures.Properties.path,
						element: <Properties />,
						handle: {
							breadcrumbs: [
								{ label: "Home", path: Routes.Dashboard.Home.path },
								{ label: "Structures", path: Routes.Dashboard.Structures.Structures.path },
								{
									label: (match: UIMatch) => `Structure ${match.params.id}`,
									path: (match: UIMatch) =>
										Routes.Dashboard.Structures.Structure.build({
											id: match.params.id as Dryp.StructureId,
										}),
								},
								{
									label: "Properties",
									path: (match: UIMatch) =>
										Routes.Dashboard.Structures.Properties.build({
											id: match.params.id as Dryp.StructureId,
										}),
								},
							],
						} satisfies CustomRouteHandle,
					},
					{
						path: Routes.Dashboard.Admin.Organizations.path,
						element: <Organizations />,
						handle: {
							breadcrumbs: [
								{ label: "Backoffice", path: Routes.Dashboard.Admin.Organizations.path },
								{ label: "Organizations", path: Routes.Dashboard.Admin.Organizations.path },
							],
						},
					},
					{
						path: Routes.Dashboard.Admin.BackofficeUsers.path,
						element: <BackofficeUsers />,
						handle: {
							breadcrumbs: [
								{ label: "Backoffice", path: Routes.Dashboard.Admin.BackofficeUsers.path },
								{ label: "Users", path: Routes.Dashboard.Admin.BackofficeUsers.path },
							],
						},
					},
					{
						path: Routes.Dashboard.Admin.Groups.path,
						element: <Groups />,
						handle: {
							breadcrumbs: [
								{ label: "Organization", path: Routes.Dashboard.Admin.Groups.path },
								{ label: "Groups", path: Routes.Dashboard.Admin.Groups.path },
							],
						},
					},
					{ path: Routes.Dashboard.Admin.Devices.path, element: <Devices /> },
					{ path: Routes.Dashboard.Admin.Billing.path, element: <Billing /> },
					{ path: Routes.Dashboard.Admin.Settings.path, element: <Settings /> },
				],
			},
			{
				element: <Empty />,
				children: [{ path: "*", element: <NotFound /> }],
			},
		],
	},
]);

export const Router = observer(() => {
	useSessionInitialization();
	const { isInitialaizing } = useStore();

	// Scrolls to top
	// TO DO: move it to its own hook
	useEffect(() => {
		const unsubscribe = router.subscribe(state => {
			if (state.historyAction === "PUSH" || state.historyAction === "REPLACE") {
				window.scrollTo(0, 0);
			}
		});
		return unsubscribe;
	}, [router]);

	if (isInitialaizing) {
		return <Initialization />;
	}

	return <RouterProvider router={router} />;
});
